import { Injectable, inject } from '@angular/core';
import { MenuItem } from '../../../core/layouts/admin-panel-layout/models/menu-item.model';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { PERMISSION } from '../../constants/permission';
import { UserType } from '../../constants/user-type-enum';
import { HelperService } from './helper.service';
import { PATIENT_PORTAL_ROUTE_PREFIX } from '../../constants/system-constants.model';
import * as ROUTES from '../../constants/route-consts';

@Injectable({
  providedIn: 'root'
})
export class AdminSideMenuService {
  private authService = inject(AuthService);
  private helperService = inject(HelperService);

  permissions$!: Observable<string[]>;
  primaryColor = this.helperService.getPrimaryColor();

  private tpaMenuItems: MenuItem[] = [
    {
      title: 'DASHBOARD.TITLE',
      routerLink: [ROUTES.ROUTE_DASHBOARD],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4102',
        pathD: 'M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'ELIGIBILITY.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-eligibility",
      subMenus: [
        {
          title: 'ELIGIBILITY.REQUEST_ELIGIBILITY',
          routerLink: [`${ROUTES.ROUTE_ELIGIBILITY}/request`],
          isActive: false,
          permission: [PERMISSION.ELIGIBILITY.SCREEN, PERMISSION.ELIGIBILITY.CREATE]
        },
        {
          title: 'ELIGIBILITY.ELIGIBILITY_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ELIGIBILITY_TRANS],
          isActive: false,
          permission: PERMISSION.ELIGIBILITY.SCREEN,
          subPathLinks: [`${ROUTES.PATH_ELIGIBILITY_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'PRESCRIPTION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-prescription",
      subMenus: [
        {
          title: 'PRESCRIPTION.PRESCRIPTION_REQUEST.TITLE',
          routerLink: [`${ROUTES.ROUTE_PRESCRIPTION}/request`],
          isActive: false,
          permission: [PERMISSION.PRESCRIPTION.SCREEN, PERMISSION.PRESCRIPTION.CREATE]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PRESCRIPTION_TRANS],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/view/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/process/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/request/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`
          ]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_LIST.PRESCRIPTION_QUEUE',
          routerLink: [ROUTES.PATH_PRESCRIPTION_QUEUE],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [`${ROUTES.PATH_PRESCRIPTION_QUEUE}/view/id`]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_LIST.MY_PRESCRIPTION',
          routerLink: [ROUTES.PATH_PRESCRIPTION_MY],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PRESCRIPTION_MY}/view/id`,
            `${ROUTES.PATH_PRESCRIPTION_MY}/process/id`,
            `${ROUTES.PATH_PRESCRIPTION_MY}/${ROUTES.SLUG_FOLLOW_UP}/request/id`
          ]
        }
      ]
    },
    {
      title: 'PREAUTH.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-preauth",
      subMenus: [
        {
          title: 'PREAUTH.PREAUTH_REQUEST.CREATE',
          routerLink: [`${ROUTES.PATH_PREAUTH_TRANS}/request`],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.CREATE],
          subPathLinks: [`${ROUTES.PATH_PREAUTH_TRANS}/request`]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subMenus: [
            {
              title: 'PREAUTH.PREAUTH_REQUEST.TITLE',
              routerLink: [`${ROUTES.PATH_PREAUTH_TRANS}/request`],
              isActive: false,
              permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.CREATE],
            }
          ],
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_TRANS}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/process/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/view/id`,
          ]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.APPROVAL_QUEUE',
          routerLink: [ROUTES.PATH_PREAUTH_QUEUE],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_QUEUE}/view/id`,
            `${ROUTES.PATH_PREAUTH_QUEUE}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_QUEUE}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.MY_APPROVAL',
          routerLink: [ROUTES.PATH_PREAUTH_MY],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_MY}/view/id`,
            `${ROUTES.PATH_PREAUTH_MY}/process/id`,
            `${ROUTES.PATH_PREAUTH_MY}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_MY}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        },
        {
          title: 'ADVANCE_PREAUTH.ADVANCE_PREAUTH_REQUEST.CREATE',
          routerLink: [`${ROUTES.PATH_ADV_PREAUTH_TRANS}/request`],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.CREATE],
          subPathLinks: [`${ROUTES.PATH_ADV_PREAUTH_TRANS}/request`]
        },
        {
          title: 'ADVANCE_PREAUTH.ADVANCE_PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ADV_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/view/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`,
          ]
        },
        {
          title: 'COMMON.HIGH_COST_ESCALATION',
          routerLink: [ROUTES.PATH_PREAUTH_REVIEW],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.HIGHCOST_ADJUDICATION],
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_REVIEW}/view/id`,
            `${ROUTES.PATH_PREAUTH_REVIEW}/id`
          ]
        }
      ]
    },
    {
      title: 'CONVERT_PREAUTH_TO_CLAIM.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-convert-preauth",
      subMenus: [
        {
          title: 'CONVERT_PREAUTH_TO_CLAIM.PREPARE_FOR_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CONVERT}/prepare-for-claims`],
          isActive: false,
          permission: PERMISSION.CLAIM_CONVERT.SCREEN,
        },
        {
          title: 'CONVERT_PREAUTH_TO_CLAIM.CONVERT_TO_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CONVERT}/convert-claims`],
          isActive: false,
          permission: PERMISSION.CLAIM_CONVERT.SCREEN,
        },
      ]
    },
    {
      title: 'CLAIM.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-claim",
      subMenus: [
        {
          title: 'CLAIM.UPLOAD_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CLAIM}/upload-claim`],
          isActive: false,
          permission: [PERMISSION.CLAIM.SCREEN, PERMISSION.CLAIM.CREATE]
        },
        {
          title: 'CLAIM.CLAIM_REQUEST.TITLE',
          routerLink: [`${ROUTES.ROUTE_CLAIM}/request`],
          isActive: false,
          permission: [PERMISSION.CLAIM.SCREEN, PERMISSION.CLAIM.CREATE]
        },
        {
          title: 'CLAIM.UPLOADS.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_UPLOAD],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_UPLOAD}/${ROUTES.SLUG_VIEW_CLAIMS}/id`,
            `${ROUTES.PATH_CLAIM_UPLOAD}/${ROUTES.SLUG_VIEW_CLAIMS}/id/view/id`,
          ]
        },
        {
          title: 'CLAIM.BATCH.PAYER_BATCH.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_BATCH],
          isActive: false,
          permission: PERMISSION.BATCH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_BATCH}/${ROUTES.SLUG_VIEW_CLAIMS}/id`,
            `${ROUTES.PATH_CLAIM_BATCH}/process/id`
          ]
        },
        {
          title: 'CLAIM.BATCH.PROVIDER_BATCH.TITLE',
          routerLink: [ROUTES.PATH_PROVIDER_BATCH],
          isActive: false,
          permission: PERMISSION.PROVIDER_BATCH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PROVIDER_BATCH}/${ROUTES.SLUG_VIEW_CLAIMS}/id`
          ]
        },
        {
          title: 'CLAIM.CLAIMS_HISTORY.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_HISTORY],
          isActive: false,
          permission: PERMISSION.CLAIM_UPLOADS.HISTORY.SCREEN,
          subPathLinks: [`${ROUTES.PATH_CLAIM_HISTORY}/${ROUTES.SLUG_UPLOAD_SUMMARY}/id`]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.SEARCH',
          routerLink: [ROUTES.PATH_CLAIM_SEARCH],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.ROUTE_CLAIM}/request/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/process/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/view/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/${ROUTES.SLUG_RELATED_CLAIM}/request/id`,
          ]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.MY_CLAIMS',
          routerLink: [ROUTES.PATH_CLAIM_MY],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_MY}/view/id`,
            `${ROUTES.PATH_CLAIM_MY}/process/id`,
            `${ROUTES.PATH_CLAIM_MY}/${ROUTES.SLUG_RELATED_CLAIM}/request/id`,
            `${ROUTES.PATH_CLAIM_MY}/process/resubmit/id/item/id`
          ]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_TRANS],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [`${ROUTES.PATH_CLAIM_TRANS}/view/id`]
        },
        {
          title: 'DASHBOARD.PATIENT_DASHBOARD.CLAIM_REIMBURSEMENTS',
          routerLink: [ROUTES.PATH_CLAIM_REIMBURSE],
          isActive: false,
          permission: PERMISSION.CLAIM_REIMBURSEMENT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_REIMBURSE}/view/id`,
            `${ROUTES.PATH_CLAIM_REIMBURSE}/create/id`
          ]
        },
      ]
    },
    {
      title: 'PREAUTH.COMMUNICATION.TITLE',
      routerLink: [ROUTES.ROUTE_COMMUNICATION],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_communication_msg',
        pathD: 'M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 13H14V15H7V13ZM7 5H17V7H7V5Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'DENIAL_MANAGEMENT.TITLE',
      routerLink: [ROUTES.ROUTE_DENIAL],
      isActive: false,
      permission: PERMISSION.DENIAL_MANAGEMENT.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4110',
        pathD: 'M3.41 6.05L5.54 8.17L9.78 3.93L11.19 5.34L5.54 11L2 7.46L3.41 6.05ZM10 13.41L7.41 16L10 18.59L8.59 20L6 17.41L3.41 20L2 18.59L4.59 16L2 13.41L3.41 12L6 14.59L8.59 12L10 13.41ZM13 9H22V7H13V9ZM13 17H22V15H13V17Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'NETWORK_MANAGEMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-network',
      subMenus: [
        {
          title: 'PROVIDER.PROVIDERS',
          routerLink: [ROUTES.PATH_NETWORK_PROVIDER],
          isActive: false,
          permission: PERMISSION.PROVIDER.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_PROVIDER}/view/id`,
            `${ROUTES.PATH_NETWORK_PROVIDER}/edit/id`,
            `${ROUTES.PATH_NETWORK_PROVIDER}/add`
          ]
        },
        {
          title: 'CONFIGURATION.PRICE_LIST.TITLE',
          routerLink: [ROUTES.PATH_NETWORK_PRICE],
          isActive: false,
          permission: PERMISSION.PRICE_LIST.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_PRICE}/add`,
            `${ROUTES.PATH_NETWORK_PRICE}/view/id`,
            `${ROUTES.PATH_NETWORK_PRICE}/edit/id`,
          ]
        },
        {
          title: 'CONTRACT.TITLE',
          routerLink: [ROUTES.PATH_NETWORK_CONTRACT],
          isActive: false,
          permission: PERMISSION.CONTRACT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_CONTRACT}/add`,
            `${ROUTES.PATH_NETWORK_CONTRACT}/view/id`,
            `${ROUTES.PATH_NETWORK_CONTRACT}/edit/id`
          ]
        },
        {
          title: 'CONFIGURATION.NETWORK.TITLE',
          routerLink: [ROUTES.PATH_NETWORKS],
          isActive: false,
          permission: PERMISSION.NETWORK.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORKS}/add`,
            `${ROUTES.PATH_NETWORKS}/view/id`,
            `${ROUTES.PATH_NETWORKS}/edit/id`,
          ]
        },
      ]
    },
    {
      title: 'POLICY_MANAGEMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIcon: {
        maskId: 'mask0_869_4129',
        pathD: 'M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H16L21 8V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V9H15V5H5V19ZM7 17H17V15H7V17ZM7 9H12V7H7V9ZM7 13H17V11H7V13Z',
        pathFillColor: this.primaryColor
      },
      subMenus: [
        {
          title: 'POLICY.TITLE',
          routerLink: [ROUTES.PATH_POLICY_POLICY],
          isActive: false,
          permission: PERMISSION.POLICY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_POLICY}/add`,
            `${ROUTES.PATH_POLICY_POLICY}/view/id`,
            `${ROUTES.PATH_POLICY_POLICY}/edit/id`,
          ]
        },
        {
          title: 'BENEFICIARY.TITLE',
          routerLink: [ROUTES.PATH_POLICY_BENEFICIARY],
          isActive: false,
          permission: PERMISSION.BENEFICIARY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_BENEFICIARY}/add`,
            `${ROUTES.PATH_POLICY_BENEFICIARY}/view/id`,
            `${ROUTES.PATH_POLICY_BENEFICIARY}/edit/id`
          ]
        },
        {
          title: 'CONFIGURATION.POLICY_CLASS.TITLE',
          routerLink: [ROUTES.PATH_POLICY_CLASS],
          isActive: false,
          permission: PERMISSION.POLICY_CLASS.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_CLASS}/add`,
            `${ROUTES.PATH_POLICY_CLASS}/view/id`,
            `${ROUTES.PATH_POLICY_CLASS}/edit/id`,
            `${ROUTES.PATH_POLICY_CLASS}/clone/id`,
          ]
        },
        {
          title: 'CONFIGURATION.BENEFIT.BENEFITS',
          routerLink: [ROUTES.PATH_POLICY_BENEFIT],
          isActive: false,
          permission: PERMISSION.BENEFIT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_BENEFIT}/add`,
            `${ROUTES.PATH_POLICY_BENEFIT}/view/id`,
            `${ROUTES.PATH_POLICY_BENEFIT}/edit/id`,
          ]
        },
      ]
    },
    {
      title: 'PAYMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-payment',
      subMenus: [
        {
          title: 'PAYMENT.CREATE.TITLE',
          routerLink: [`${ROUTES.ROUTE_PAYMENT}/create`],
          isActive: false,
          permission: [PERMISSION.PAYMENT.SCREEN],
        },
        {
          title: 'PAYMENT.LIST.TITLE',
          routerLink: [ROUTES.PATH_PAYMENT_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT.SCREEN, PERMISSION.PAYMENT.LIST],
          subPathLinks: [
            `${ROUTES.PATH_PAYMENT_TRANS}/view/id`,
            `${ROUTES.PATH_PAYMENT_TRANS}/edit/id`
          ]
        }
      ]
    },
    {
      title: 'PAYMENT_RECONCILIATION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-reconciliation',
      subMenus: [
        {
          title: 'PAYMENT_RECONCILIATION.CREATE_RECONCILIATION.TITLE',
          routerLink: [`${ROUTES.ROUTE_RECONCILIATION}/create`],
          isActive: false,
          permission: [PERMISSION.PAYMENT_RECONCILIATION.SCREEN, PERMISSION.PAYMENT_RECONCILIATION.CREATE],
        },
        {
          title: 'PAYMENT_RECONCILIATION.RECONCILIATION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_RECONCILIATION_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT_RECONCILIATION.SCREEN, PERMISSION.PAYMENT_RECONCILIATION.LIST],
          subPathLinks: [`${ROUTES.PATH_RECONCILIATION_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'INSURANCE_COMPANY.TITLE',
      routerLink: [ROUTES.ROUTE_INSURANCE],
      isActive: false,
      permission: PERMISSION.INSURANCE_COMPANY.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4109',
        pathD: 'M2 21V3H12V7H22V21H2ZM4 19H10V17H4V19ZM4 15H10V13H4V15ZM4 11H10V9H4V11ZM4 7H10V5H4V7ZM12 19H20V9H12V19ZM14 13V11H18V13H14ZM14 17V15H18V17H14Z',
        pathFillColor: this.primaryColor
      },
      subPathLinks: [
        `${ROUTES.ROUTE_INSURANCE}/add`,
        `${ROUTES.ROUTE_INSURANCE}/view/id`,
        `${ROUTES.ROUTE_INSURANCE}/edit/id`
      ]
    },
    {
      title: 'CONFIGURATION.TITLE',
      routerLink: [ROUTES.ROUTE_CONFIG],
      isActive: false,
      svgIconUrl: 'custom-discover-tune',
      subMenus: [
        {
          title: 'COMMON.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_MASTER],
          isActive: false,
          permission: PERMISSION.MASTER_DATA.SCREEN,
        },
        {
          title: 'CONFIGURATION.COMMONLOV.LIST',
          routerLink: [ROUTES.PATH_CONFIG_LOV],
          isActive: false,
          permission: PERMISSION.COMMON_LOV.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_LOV}/add`,
            `${ROUTES.PATH_CONFIG_LOV}/edit/id`,
            `${ROUTES.PATH_CONFIG_LOV}/view/id`,
          ]
        },
        {
          title: 'CONFIGURATION.CLAIM_TYPE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_CLAIM_TYPE],
          isActive: false,
          permission: PERMISSION.CLAIM_TYPE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_CLAIM_TYPE}/edit/id`,
            `${ROUTES.PATH_CONFIG_CLAIM_TYPE}/view/id`,
            `${ROUTES.PATH_CONFIG_CLAIM_TYPE}/add`
          ]
        },
        {
          title: 'CONFIGURATION.REASON.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_REASON],
          isActive: false,
          permission: PERMISSION.REASON.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_REASON}/view/id`,
            `${ROUTES.PATH_CONFIG_REASON}/edit/id`,
            `${ROUTES.PATH_CONFIG_REASON}/add`,
          ]
        },
        {
          title: 'CONFIGURATION.ENCOUNTER_CLASS.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_ENCOUNTER],
          isActive: false,
          permission: PERMISSION.ENCOUNTER_CLASS.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_ENCOUNTER}/view/id`,
            `${ROUTES.PATH_CONFIG_ENCOUNTER}/edit/id`,
            `${ROUTES.PATH_CONFIG_ENCOUNTER}/add`,
          ]
        },
        {
          title: 'CONFIGURATION.MORPHOLOGY.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_MORPHOLOGY],
          isActive: false,
          permission: PERMISSION.MORPHOLOGY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_MORPHOLOGY}/view/id`,
            `${ROUTES.PATH_CONFIG_MORPHOLOGY}/edit/id`,
            `${ROUTES.PATH_CONFIG_MORPHOLOGY}/add`
          ]
        },
        {
          title: 'CONFIGURATION.SERVICE_TYPE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_SERVICE_TYPE],
          isActive: false,
          permission: PERMISSION.SERVICE_TYPE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_SERVICE_TYPE}/view/id`,
            `${ROUTES.PATH_CONFIG_SERVICE_TYPE}/edit/id`,
            `${ROUTES.PATH_CONFIG_SERVICE_TYPE}/add`
          ]
        },
        {
          title: 'CONFIGURATION.SERVICE_CODE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_SERVICE_CODE],
          isActive: false,
          permission: PERMISSION.SERVICE_CODE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_SERVICE_CODE}/view/id`,
            `${ROUTES.PATH_CONFIG_SERVICE_CODE}/edit/id`,
            `${ROUTES.PATH_CONFIG_SERVICE_CODE}/add`
          ]
        },
        {
          title: 'CONFIGURATION.LOINC_CODE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_LOINC],
          isActive: false,
          permission: PERMISSION.LOINC_CODE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_LOINC}/view/id`,
            `${ROUTES.PATH_CONFIG_LOINC}/edit/id`,
            `${ROUTES.PATH_CONFIG_LOINC}/add`
          ]
        },
        {
          title: 'CONFIGURATION.PHYSICIAN.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_PHYSICIAN],
          isActive: false,
          permission: PERMISSION.PHYSICIAN.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_PHYSICIAN}/view/id`,
            `${ROUTES.PATH_CONFIG_PHYSICIAN}/edit/id`,
            `${ROUTES.PATH_CONFIG_PHYSICIAN}/add`
          ]
        },
        {
          title: 'CONFIGURATION.DIAGNOSIS.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_DIAGNOSIS],
          isActive: false,
          permission: PERMISSION.DIAGNOSIS_CODE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_DIAGNOSIS}/view/id`,
            `${ROUTES.PATH_CONFIG_DIAGNOSIS}/edit/id`,
            `${ROUTES.PATH_CONFIG_DIAGNOSIS}/add`
          ]
        },
        {
          title: 'CONFIGURATION.SPECIALITY.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_SPECIALITY],
          isActive: false,
          permission: PERMISSION.SPECIALITY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_SPECIALITY}/view/id`,
            `${ROUTES.PATH_CONFIG_SPECIALITY}/edit/id`,
            `${ROUTES.PATH_CONFIG_SPECIALITY}/add`
          ]
        },
        {
          title: 'CONFIGURATION.BODYSUBSITE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_BODY_SUB],
          isActive: false,
          permission: PERMISSION.BODY_SUB_SITE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_BODY_SUB}/view/id`,
            `${ROUTES.PATH_CONFIG_BODY_SUB}/edit/id`,
            `${ROUTES.PATH_CONFIG_BODY_SUB}/add`
          ]
        },
        {
          title: 'CONFIGURATION.MASTER_DATA.BODY_SITE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_BODY_SITE],
          isActive: false,
          permission: PERMISSION.BODY_SITE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_BODY_SITE}/view/id`,
            `${ROUTES.PATH_CONFIG_BODY_SITE}/edit/id`,
            `${ROUTES.PATH_CONFIG_BODY_SITE}/add`
          ]
        },
        {
          title: 'CONFIGURATION.PHARMACEUTICAL_FORM.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_PHARMA_FORM],
          isActive: false,
          permission: PERMISSION.PHARMACEUTICAL_FORM.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_PHARMA_FORM}/view/id`,
            `${ROUTES.PATH_CONFIG_PHARMA_FORM}/edit/id`,
            `${ROUTES.PATH_CONFIG_PHARMA_FORM}/add`
          ]
        },
        {
          title: 'CONFIGURATION.DOSAGE_TYPE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_DOSAGE],
          isActive: false,
          permission: PERMISSION.DOSAGE_TYPE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_DOSAGE}/view/id`,
            `${ROUTES.PATH_CONFIG_DOSAGE}/edit/id`,
            `${ROUTES.PATH_CONFIG_DOSAGE}/add`
          ]
        },
        {
          title: 'CONFIGURATION.UCUM_CODE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_UCUM],
          isActive: false,
          permission: PERMISSION.UCUM_CODE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_UCUM}/add`,
            `${ROUTES.PATH_CONFIG_UCUM}/view/id`,
            `${ROUTES.PATH_CONFIG_UCUM}/edit/id`,
          ]
        },
        {
          title: 'CONFIGURATION.MEDICAL_DEVICE.TITLE',
          routerLink: [ROUTES.PATH_CONFIG_DEVICE],
          isActive: false,
          permission: PERMISSION.MEDICAL_DEVICE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CONFIG_DEVICE}/add`,
            `${ROUTES.PATH_CONFIG_DEVICE}/view/id`,
            `${ROUTES.PATH_CONFIG_DEVICE}/edit/id`,
          ]
        }
      ]
    },
    {
      title: 'REPORTS.TITLE',
      routerLink: [ROUTES.ROUTE_REPORTS],
      isActive: false,
      svgIconUrl: 'custom-chart-data',
      subMenus: [
        {
          title: 'REPORTS.TITLE',
          routerLink: [ROUTES.ROUTE_REPORTS],
          isActive: false,
          permission: [PERMISSION.REPORTS.SCREEN]
        },
        {
          title: 'COMMON.EXTERNAL_CHART',
          routerLink: [`${ROUTES.PATH_ROUTE_EXTERNAL}`],
          isActive: false,
          permission: [PERMISSION.CHART_SETTINGS.SCREEN]
        }
      ]
    },
    {
      title: 'ADMINISTRATOR.TITLE',
      routerLink: [ROUTES.ROUTE_ADMIN],
      isActive: false,
      svgIcon: {
        maskId: 'mask0_869_4147',
        pathD: 'M5.85 17.1C6.7 16.45 7.65 15.9375 8.7 15.5625C9.75 15.1875 10.85 15 12 15C13.15 15 14.25 15.1875 15.3 15.5625C16.35 15.9375 17.3 16.45 18.15 17.1C18.7333 16.4167 19.1875 15.6417 19.5125 14.775C19.8375 13.9083 20 12.9833 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 12.9833 4.1625 13.9083 4.4875 14.775C4.8125 15.6417 5.26667 16.4167 5.85 17.1ZM12 13C11.0167 13 10.1875 12.6625 9.5125 11.9875C8.8375 11.3125 8.5 10.4833 8.5 9.5C8.5 8.51667 8.8375 7.6875 9.5125 7.0125C10.1875 6.3375 11.0167 6 12 6C12.9833 6 13.8125 6.3375 14.4875 7.0125C15.1625 7.6875 15.5 8.51667 15.5 9.5C15.5 10.4833 15.1625 11.3125 14.4875 11.9875C13.8125 12.6625 12.9833 13 12 13ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.8833 20 13.7167 19.8708 14.5 19.6125C15.2833 19.3542 16 18.9833 16.65 18.5C16 18.0167 15.2833 17.6458 14.5 17.3875C13.7167 17.1292 12.8833 17 12 17C11.1167 17 10.2833 17.1292 9.5 17.3875C8.71667 17.6458 8 18.0167 7.35 18.5C8 18.9833 8.71667 19.3542 9.5 19.6125C10.2833 19.8708 11.1167 20 12 20ZM12 11C12.4333 11 12.7917 10.8583 13.075 10.575C13.3583 10.2917 13.5 9.93333 13.5 9.5C13.5 9.06667 13.3583 8.70833 13.075 8.425C12.7917 8.14167 12.4333 8 12 8C11.5667 8 11.2083 8.14167 10.925 8.425C10.6417 8.70833 10.5 9.06667 10.5 9.5C10.5 9.93333 10.6417 10.2917 10.925 10.575C11.2083 10.8583 11.5667 11 12 11Z',
        pathFillColor: this.primaryColor
      },
      subMenus: [
        {
          title: 'ADMINISTRATOR.USER.TITLE',
          routerLink: [ROUTES.PATH_ADMIN_USERS],
          isActive: false,
          permission: [PERMISSION.USER.SCREEN, PERMISSION.USER.LIST],
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_USERS}/edit/id`,
            `${ROUTES.PATH_ADMIN_USERS}/view/id`,
            `${ROUTES.PATH_ADMIN_USERS}/add`,
          ]
        },
        {
          title: 'ADMINISTRATOR.ROLE.TITLE',
          routerLink: [`${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}`],
          isActive: false,
          permission: PERMISSION.ROLE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/add`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/clone/id`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/edit/id`
          ]
        },
        {
          title: 'ADMINISTRATOR.PERMISSION.TITLE',
          routerLink: [`${ROUTES.PATH_ADMIN_ROLES}/permissions`],
          isActive: false,
          permission: PERMISSION.PERMISSION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_PERMISSIONS}/edit/id`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_PERMISSIONS}/add`
          ]
        },
        {
          title: 'ADMINISTRATOR.AUDIT_LOG.TITLE',
          routerLink: [ROUTES.PATH_ADMIN_AUDIT],
          isActive: false,
          permission: PERMISSION.AUDIT_LOG.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_AUDIT}/details/id`
          ]
        },
        {
          title: 'ADMINISTRATOR.FAQ.TITLE',
          routerLink: [ROUTES.PATH_ADMIN_FAQ],
          isActive: false,
          permission: PERMISSION.FAQ.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_FAQ}/edit/id`,
            `${ROUTES.PATH_ADMIN_FAQ}/add`,
            `${ROUTES.PATH_ADMIN_FAQ}/view/id`,
          ]
        }
      ]
    },
    {
      title: 'ADMINISTRATOR.USER.INFO',
      routerLink: [ROUTES.ROUTE_USER_INFO],
      isActive: false,
      excludeForSidemenu: true
    },
    {
      title: 'NOTIFICATION.TTILE',
      routerLink: [`/${ROUTES.SLUG_NOTIFICATION}`],
      isActive: false,
      checkAnyPermisison: true,
      permission: [PERMISSION.PROVIDER_NOTIFICATION.SCREEN, PERMISSION.PAYER_NOTIFICATION.SCREEN],
      svgIconUrl: 'custom-notifications'
    },
    {
      title: 'COMMON.SETTINGS',
      routerLink: [ROUTES.ROUTE_SETTINGS],
      isActive: false,
      permission: PERMISSION.SYSTEM_SETTINGS.SCREEN,
      svgIconUrl: "settings",
      subPathLinks: [
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting`,
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting/add`,
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting/view`,
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting/edit`
      ],
    }
  ];

  private providerMenuItems: MenuItem[] = [
    {
      title: 'DASHBOARD.TITLE',
      routerLink: [ROUTES.ROUTE_DASHBOARD],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4102',
        pathD: 'M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'PREAUTH.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-preauth",
      subMenus: [
        {
          title: 'PREAUTH.PREAUTH_REQUEST.CREATE',
          routerLink: [`${ROUTES.PATH_PREAUTH_TRANS}/request`],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.CREATE]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_TRANS}/view/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/process/id`
          ]
        },
        {
          title: 'ADVANCE_PREAUTH.ADVANCE_PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ADV_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/view/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        }
      ]
    },
    {
      title: 'ELIGIBILITY.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-eligibility",
      subMenus: [
        {
          title: 'ELIGIBILITY.REQUEST_ELIGIBILITY',
          routerLink: [`${ROUTES.ROUTE_ELIGIBILITY}/request`],
          isActive: false,
          permission: [PERMISSION.ELIGIBILITY.SCREEN, PERMISSION.ELIGIBILITY.CREATE]
        },
        {
          title: 'ELIGIBILITY.ELIGIBILITY_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ELIGIBILITY_TRANS],
          isActive: false,
          permission: PERMISSION.ELIGIBILITY.SCREEN,
          subPathLinks: [`${ROUTES.PATH_ELIGIBILITY_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'PRESCRIPTION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-prescription",
      subMenus: [
        {
          title: 'PRESCRIPTION.PRESCRIPTION_REQUEST.TITLE',
          routerLink: [`${ROUTES.ROUTE_PRESCRIPTION}/request`],
          isActive: false,
          permission: [PERMISSION.PRESCRIPTION.SCREEN, PERMISSION.PRESCRIPTION.CREATE]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PRESCRIPTION_TRANS],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/view/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/process/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/request/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        }
      ]
    },
    {
      title: 'CONVERT_PREAUTH_TO_CLAIM.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-convert-preauth",
      subMenus: [
        {
          title: 'CONVERT_PREAUTH_TO_CLAIM.PREPARE_FOR_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CONVERT}/prepare-for-claims`],
          isActive: false,
          permission: PERMISSION.CLAIM_CONVERT.SCREEN,
        },
        {
          title: 'CONVERT_PREAUTH_TO_CLAIM.CONVERT_TO_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CONVERT}/convert-claims`],
          isActive: false,
          permission: PERMISSION.CLAIM_CONVERT.SCREEN,
        },
      ]
    },
    {
      title: 'CLAIM.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-claim",
      subMenus: [
        {
          title: 'CLAIM.UPLOAD_CLAIMS.TITLE',
          routerLink: [`${ROUTES.ROUTE_CLAIM}/upload-claim`],
          isActive: false,
          permission: [PERMISSION.CLAIM.SCREEN, PERMISSION.CLAIM.CREATE]
        },
        {
          title: 'CLAIM.CLAIM_REQUEST.TITLE',
          routerLink: [`${ROUTES.ROUTE_CLAIM}/request`],
          isActive: false,
          permission: [PERMISSION.CLAIM.SCREEN, PERMISSION.CLAIM.CREATE]
        },
        {
          title: 'CLAIM.UPLOADS.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_UPLOAD],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_UPLOAD}/${ROUTES.SLUG_VIEW_CLAIMS}/id`,
            `${ROUTES.PATH_CLAIM_UPLOAD}/view/id`,
          ]
        },
        {
          title: 'CLAIM.BATCH.TITLE',
          routerLink: [ROUTES.PATH_PROVIDER_BATCH],
          isActive: false,
          permission: PERMISSION.PROVIDER_BATCH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PROVIDER_BATCH}/${ROUTES.SLUG_VIEW_CLAIMS}/id`
          ]
        },
        {
          title: 'CLAIM.CLAIMS_HISTORY.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_HISTORY],
          isActive: false,
          permission: PERMISSION.CLAIM_UPLOADS.HISTORY.SCREEN,
          subPathLinks: [`${ROUTES.PATH_CLAIM_HISTORY}/${ROUTES.SLUG_UPLOAD_SUMMARY}/id`]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.SEARCH',
          routerLink: [ROUTES.PATH_CLAIM_SEARCH],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.ROUTE_CLAIM}/request/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/view/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/${ROUTES.SLUG_RELATED_CLAIM}/request/id`
          ]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_TRANS],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [`${ROUTES.PATH_CLAIM_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'PREAUTH.COMMUNICATION.TITLE',
      routerLink: [ROUTES.ROUTE_COMMUNICATION],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_communication_msg',
        pathD: 'M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 13H14V15H7V13ZM7 5H17V7H7V5Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'DENIAL_MANAGEMENT.TITLE',
      routerLink: [ROUTES.ROUTE_DENIAL],
      isActive: false,
      permission: PERMISSION.DENIAL_MANAGEMENT.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4110',
        pathD: 'M3.41 6.05L5.54 8.17L9.78 3.93L11.19 5.34L5.54 11L2 7.46L3.41 6.05ZM10 13.41L7.41 16L10 18.59L8.59 20L6 17.41L3.41 20L2 18.59L4.59 16L2 13.41L3.41 12L6 14.59L8.59 12L10 13.41ZM13 9H22V7H13V9ZM13 17H22V15H13V17Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'PAYMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-payment',
      subMenus: [
        {
          title: 'PAYMENT.LIST.TITLE',
          routerLink: [ROUTES.PATH_PAYMENT_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT.SCREEN, PERMISSION.PAYMENT.LIST],
          subPathLinks: [
            `${ROUTES.PATH_PAYMENT_TRANS}/view/id`,
            `${ROUTES.PATH_PAYMENT_TRANS}/edit/id`
          ]
        }
      ]
    },
    {
      title: 'PAYMENT_RECONCILIATION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-reconciliation',
      subMenus: [
        {
          title: 'PAYMENT_RECONCILIATION.RECONCILIATION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_RECONCILIATION_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT_RECONCILIATION.SCREEN, PERMISSION.PAYMENT_RECONCILIATION.LIST],
          subPathLinks: [`${ROUTES.PATH_RECONCILIATION_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'POLICY.TITLE',
      routerLink: [ROUTES.ROUTE_PROV_POLICY],
      isActive: false,
      excludeForSidemenu: true,
      permission: PERMISSION.POLICY.SCREEN,
      subPathLinks: [
        `${ROUTES.ROUTE_PROV_POLICY}/view/id`,
      ]
    },
    {
      title: 'BENEFICIARY.TITLE',
      routerLink: [ROUTES.ROUTE_PROV_BENEFICIARY],
      isActive: false,
      permission: PERMISSION.BENEFICIARY.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4123',
        pathD: 'M0 18V16.425C0 15.7083 0.366667 15.125 1.1 14.675C1.83333 14.225 2.8 14 4 14C4.21667 14 4.425 14.0042 4.625 14.0125C4.825 14.0208 5.01667 14.0417 5.2 14.075C4.96667 14.425 4.79167 14.7917 4.675 15.175C4.55833 15.5583 4.5 15.9583 4.5 16.375V18H0ZM6 18V16.375C6 15.8417 6.14583 15.3542 6.4375 14.9125C6.72917 14.4708 7.14167 14.0833 7.675 13.75C8.20833 13.4167 8.84583 13.1667 9.5875 13C10.3292 12.8333 11.1333 12.75 12 12.75C12.8833 12.75 13.6958 12.8333 14.4375 13C15.1792 13.1667 15.8167 13.4167 16.35 13.75C16.8833 14.0833 17.2917 14.4708 17.575 14.9125C17.8583 15.3542 18 15.8417 18 16.375V18H6ZM19.5 18V16.375C19.5 15.9417 19.4458 15.5333 19.3375 15.15C19.2292 14.7667 19.0667 14.4083 18.85 14.075C19.0333 14.0417 19.2208 14.0208 19.4125 14.0125C19.6042 14.0042 19.8 14 20 14C21.2 14 22.1667 14.2208 22.9 14.6625C23.6333 15.1042 24 15.6917 24 16.425V18H19.5ZM8.125 16H15.9C15.7333 15.6667 15.2708 15.375 14.5125 15.125C13.7542 14.875 12.9167 14.75 12 14.75C11.0833 14.75 10.2458 14.875 9.4875 15.125C8.72917 15.375 8.275 15.6667 8.125 16ZM4 13C3.45 13 2.97917 12.8042 2.5875 12.4125C2.19583 12.0208 2 11.55 2 11C2 10.4333 2.19583 9.95833 2.5875 9.575C2.97917 9.19167 3.45 9 4 9C4.56667 9 5.04167 9.19167 5.425 9.575C5.80833 9.95833 6 10.4333 6 11C6 11.55 5.80833 12.0208 5.425 12.4125C5.04167 12.8042 4.56667 13 4 13ZM20 13C19.45 13 18.9792 12.8042 18.5875 12.4125C18.1958 12.0208 18 11.55 18 11C18 10.4333 18.1958 9.95833 18.5875 9.575C18.9792 9.19167 19.45 9 20 9C20.5667 9 21.0417 9.19167 21.425 9.575C21.8083 9.95833 22 10.4333 22 11C22 11.55 21.8083 12.0208 21.425 12.4125C21.0417 12.8042 20.5667 13 20 13ZM12 12C11.1667 12 10.4583 11.7083 9.875 11.125C9.29167 10.5417 9 9.83333 9 9C9 8.15 9.29167 7.4375 9.875 6.8625C10.4583 6.2875 11.1667 6 12 6C12.85 6 13.5625 6.2875 14.1375 6.8625C14.7125 7.4375 15 8.15 15 9C15 9.83333 14.7125 10.5417 14.1375 11.125C13.5625 11.7083 12.85 12 12 12ZM12 10C12.2833 10 12.5208 9.90417 12.7125 9.7125C12.9042 9.52083 13 9.28333 13 9C13 8.71667 12.9042 8.47917 12.7125 8.2875C12.5208 8.09583 12.2833 8 12 8C11.7167 8 11.4792 8.09583 11.2875 8.2875C11.0958 8.47917 11 8.71667 11 9C11 9.28333 11.0958 9.52083 11.2875 9.7125C11.4792 9.90417 11.7167 10 12 10Z',
        pathFillColor: this.primaryColor
      },
      subPathLinks: [
        `${ROUTES.ROUTE_PROV_BENEFICIARY}/view/id`,
        `${ROUTES.ROUTE_PROV_BENEFICIARY}/add`
      ]
    },
    {
      title: 'REPORTS.TITLE',
      routerLink: [ROUTES.ROUTE_REPORTS],
      isActive: false,
      permission: [PERMISSION.REPORTS.SCREEN],
      svgIconUrl: 'custom-chart-data',
    },
    {
      title: 'ADMINISTRATOR.TITLE',
      routerLink: [ROUTES.ROUTE_ADMIN],
      isActive: false,
      svgIcon: {
        maskId: 'mask0_869_4147',
        pathD: 'M5.85 17.1C6.7 16.45 7.65 15.9375 8.7 15.5625C9.75 15.1875 10.85 15 12 15C13.15 15 14.25 15.1875 15.3 15.5625C16.35 15.9375 17.3 16.45 18.15 17.1C18.7333 16.4167 19.1875 15.6417 19.5125 14.775C19.8375 13.9083 20 12.9833 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 12.9833 4.1625 13.9083 4.4875 14.775C4.8125 15.6417 5.26667 16.4167 5.85 17.1ZM12 13C11.0167 13 10.1875 12.6625 9.5125 11.9875C8.8375 11.3125 8.5 10.4833 8.5 9.5C8.5 8.51667 8.8375 7.6875 9.5125 7.0125C10.1875 6.3375 11.0167 6 12 6C12.9833 6 13.8125 6.3375 14.4875 7.0125C15.1625 7.6875 15.5 8.51667 15.5 9.5C15.5 10.4833 15.1625 11.3125 14.4875 11.9875C13.8125 12.6625 12.9833 13 12 13ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.8833 20 13.7167 19.8708 14.5 19.6125C15.2833 19.3542 16 18.9833 16.65 18.5C16 18.0167 15.2833 17.6458 14.5 17.3875C13.7167 17.1292 12.8833 17 12 17C11.1167 17 10.2833 17.1292 9.5 17.3875C8.71667 17.6458 8 18.0167 7.35 18.5C8 18.9833 8.71667 19.3542 9.5 19.6125C10.2833 19.8708 11.1167 20 12 20ZM12 11C12.4333 11 12.7917 10.8583 13.075 10.575C13.3583 10.2917 13.5 9.93333 13.5 9.5C13.5 9.06667 13.3583 8.70833 13.075 8.425C12.7917 8.14167 12.4333 8 12 8C11.5667 8 11.2083 8.14167 10.925 8.425C10.6417 8.70833 10.5 9.06667 10.5 9.5C10.5 9.93333 10.6417 10.2917 10.925 10.575C11.2083 10.8583 11.5667 11 12 11Z',
        pathFillColor: this.primaryColor
      },
      subMenus: [
        {
          title: 'ADMINISTRATOR.USER.TITLE',
          routerLink: [ROUTES.PATH_ADMIN_USERS],
          isActive: false,
          permission: [PERMISSION.USER.SCREEN, PERMISSION.USER.LIST],
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_USERS}/edit/id`,
            `${ROUTES.PATH_ADMIN_USERS}/view/id`,
            `${ROUTES.PATH_ADMIN_USERS}/add`,
          ]
        },
        {
          title: 'ADMINISTRATOR.ROLE.TITLE',
          routerLink: [`${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}`],
          isActive: false,
          permission: PERMISSION.ROLE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/add`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/clone/id`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/edit/id`
          ]
        }
      ]
    },
    {
      title: 'ADMINISTRATOR.USER.INFO',
      routerLink: [ROUTES.ROUTE_USER_INFO],
      isActive: false,
      excludeForSidemenu: true
    },
    {
      title: 'NOTIFICATION.TTILE',
      routerLink: [`/${ROUTES.SLUG_NOTIFICATION}`],
      isActive: false,
      permission: PERMISSION.PROVIDER_NOTIFICATION.SCREEN,
      svgIconUrl: 'custom-notifications'
    },
    {
      title: 'COMMON.SETTINGS',
      routerLink: [ROUTES.ROUTE_SETTINGS],
      isActive: false,
      permission: PERMISSION.SYSTEM_SETTINGS.SCREEN,
      svgIconUrl: "settings",
      subPathLinks: [
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting`,
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting/view`
      ],
    }
  ];

  private payerMenuItems: MenuItem[] = [
    {
      title: 'DASHBOARD.TITLE',
      routerLink: [ROUTES.ROUTE_DASHBOARD],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4102',
        pathD: 'M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'ELIGIBILITY.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-eligibility",
      subMenus: [
        {
          title: 'ELIGIBILITY.REQUEST_ELIGIBILITY',
          routerLink: [`${ROUTES.ROUTE_ELIGIBILITY}/request`],
          isActive: false,
          permission: [PERMISSION.ELIGIBILITY.SCREEN, PERMISSION.ELIGIBILITY.CREATE]
        },
        {
          title: 'ELIGIBILITY.ELIGIBILITY_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ELIGIBILITY_TRANS],
          isActive: false,
          permission: PERMISSION.ELIGIBILITY.SCREEN,
          subPathLinks: [`${ROUTES.PATH_ELIGIBILITY_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'PRESCRIPTION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-prescription",
      subMenus: [
        {
          title: 'PRESCRIPTION.PRESCRIPTION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PRESCRIPTION_TRANS],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/view/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/process/id`,
            `${ROUTES.PATH_PRESCRIPTION_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`
          ]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_LIST.PRESCRIPTION_QUEUE',
          routerLink: [ROUTES.PATH_PRESCRIPTION_QUEUE],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [`${ROUTES.PATH_PRESCRIPTION_QUEUE}/view/id`]
        },
        {
          title: 'PRESCRIPTION.PRESCRIPTION_LIST.MY_PRESCRIPTION',
          routerLink: [ROUTES.PATH_PRESCRIPTION_MY],
          isActive: false,
          permission: PERMISSION.PRESCRIPTION.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PRESCRIPTION_MY}/view/id`,
            `${ROUTES.PATH_PRESCRIPTION_MY}/process/id`,
            `${ROUTES.PATH_PRESCRIPTION_MY}/${ROUTES.SLUG_FOLLOW_UP}/request/id`
          ]
        }
      ]
    },
    {
      title: 'PREAUTH.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-preauth",
      subMenus: [
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_TRANS}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/process/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`,
            `${ROUTES.PATH_PREAUTH_TRANS}/view/id`,
          ]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.APPROVAL_QUEUE',
          routerLink: [ROUTES.PATH_PREAUTH_QUEUE],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_QUEUE}/view/id`,
            `${ROUTES.PATH_PREAUTH_QUEUE}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_QUEUE}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        },
        {
          title: 'PREAUTH.PREAUTH_TRANSACTION.MY_APPROVAL',
          routerLink: [ROUTES.PATH_PREAUTH_MY],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_MY}/view/id`,
            `${ROUTES.PATH_PREAUTH_MY}/process/id`,
            `${ROUTES.PATH_PREAUTH_MY}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_PREAUTH_MY}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        },
        {
          title: 'ADVANCE_PREAUTH.ADVANCE_PREAUTH_REQUEST.CREATE',
          routerLink: [`${ROUTES.PATH_ADV_PREAUTH_TRANS}/request`],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.CREATE],
          subPathLinks: [`${ROUTES.PATH_ADV_PREAUTH_TRANS}/request`]
        },
        {
          title: 'ADVANCE_PREAUTH.ADVANCE_PREAUTH_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_ADV_PREAUTH_TRANS],
          isActive: false,
          permission: PERMISSION.PRE_AUTH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/view/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_FOLLOW_UP}/request/id`,
            `${ROUTES.PATH_ADV_PREAUTH_TRANS}/${ROUTES.SLUG_REJECT}/request/id`
          ]
        },
        {
          title: 'COMMON.HIGH_COST_ESCALATION',
          routerLink: [ROUTES.PATH_PREAUTH_REVIEW],
          isActive: false,
          permission: [PERMISSION.PRE_AUTH.SCREEN, PERMISSION.PRE_AUTH.HIGHCOST_ADJUDICATION],
          subPathLinks: [
            `${ROUTES.PATH_PREAUTH_REVIEW}/view/id`,
            `${ROUTES.PATH_PREAUTH_REVIEW}/id`
          ]
        }
      ]
    },
    {
      title: 'CLAIM.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: "custom-claim",
      subMenus: [
        {
          title: 'CLAIM.CLAIM_TRANSACTION.SEARCH',
          routerLink: [ROUTES.PATH_CLAIM_SEARCH],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.ROUTE_CLAIM}/request/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/process/id`,
            `${ROUTES.PATH_CLAIM_SEARCH}/view/id`
          ]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.MY_CLAIMS',
          routerLink: [ROUTES.PATH_CLAIM_MY],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_MY}/view/id`,
            `${ROUTES.PATH_CLAIM_MY}/process/id`,
            `${ROUTES.PATH_CLAIM_MY}/process/resubmit/id/item/id`
          ]
        },
        {
          title: 'CLAIM.CLAIM_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_TRANS],
          isActive: false,
          permission: PERMISSION.CLAIM.SCREEN,
          subPathLinks: [`${ROUTES.PATH_CLAIM_TRANS}/view/id`]
        },
        {
          title: 'DASHBOARD.PATIENT_DASHBOARD.CLAIM_REIMBURSEMENTS',
          routerLink: [ROUTES.PATH_CLAIM_REIMBURSE],
          isActive: false,
          permission: PERMISSION.CLAIM_REIMBURSEMENT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_REIMBURSE}/view/id`,
            `${ROUTES.PATH_CLAIM_REIMBURSE}/create/id`
          ]
        },
        {
          title: 'CLAIM.BATCH.TITLE',
          routerLink: [ROUTES.PATH_CLAIM_BATCH],
          isActive: false,
          permission: PERMISSION.BATCH.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_CLAIM_BATCH}/${ROUTES.SLUG_VIEW_CLAIMS}/id`,
            `${ROUTES.PATH_CLAIM_BATCH}/process/id`
          ]
        }
      ]
    },
    {
      title: 'PREAUTH.COMMUNICATION.TITLE',
      routerLink: [ROUTES.ROUTE_COMMUNICATION],
      isActive: false,
      permission: PERMISSION.DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_communication_msg',
        pathD: 'M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 13H14V15H7V13ZM7 5H17V7H7V5Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'DENIAL_MANAGEMENT.TITLE',
      routerLink: [ROUTES.ROUTE_DENIAL],
      isActive: false,
      permission: PERMISSION.DENIAL_MANAGEMENT.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4110',
        pathD: 'M3.41 6.05L5.54 8.17L9.78 3.93L11.19 5.34L5.54 11L2 7.46L3.41 6.05ZM10 13.41L7.41 16L10 18.59L8.59 20L6 17.41L3.41 20L2 18.59L4.59 16L2 13.41L3.41 12L6 14.59L8.59 12L10 13.41ZM13 9H22V7H13V9ZM13 17H22V15H13V17Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'NETWORK_MANAGEMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-network',
      subMenus: [
        {
          title: 'PROVIDER.PROVIDERS',
          routerLink: [ROUTES.PATH_NETWORK_PROVIDER],
          isActive: false,
          permission: PERMISSION.PROVIDER.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_PROVIDER}/view/id`,
            `${ROUTES.PATH_NETWORK_PROVIDER}/edit/id`,
            `${ROUTES.PATH_NETWORK_PROVIDER}/add`
          ]
        },
        {
          title: 'CONFIGURATION.PRICE_LIST.TITLE',
          routerLink: [ROUTES.PATH_NETWORK_PRICE],
          isActive: false,
          permission: PERMISSION.PRICE_LIST.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_PRICE}/add`,
            `${ROUTES.PATH_NETWORK_PRICE}/view/id`,
            `${ROUTES.PATH_NETWORK_PRICE}/edit/id`,
          ]
        },
        {
          title: 'CONTRACT.TITLE',
          routerLink: [ROUTES.PATH_NETWORK_CONTRACT],
          isActive: false,
          permission: PERMISSION.CONTRACT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORK_CONTRACT}/add`,
            `${ROUTES.PATH_NETWORK_CONTRACT}/view/id`,
            `${ROUTES.PATH_NETWORK_CONTRACT}/edit/id`
          ]
        },
        {
          title: 'CONFIGURATION.NETWORK.TITLE',
          routerLink: [ROUTES.PATH_NETWORKS],
          isActive: false,
          permission: PERMISSION.NETWORK.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_NETWORKS}/add`,
            `${ROUTES.PATH_NETWORKS}/view/id`,
            `${ROUTES.PATH_NETWORKS}/edit/id`,
          ]
        },
      ]
    },
    {
      title: 'POLICY_MANAGEMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIcon: {
        maskId: 'mask0_869_4129',
        pathD: 'M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H16L21 8V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V9H15V5H5V19ZM7 17H17V15H7V17ZM7 9H12V7H7V9ZM7 13H17V11H7V13Z',
        pathFillColor: this.primaryColor
      },
      subMenus: [
        {
          title: 'POLICY.TITLE',
          routerLink: [ROUTES.PATH_POLICY_POLICY],
          isActive: false,
          permission: PERMISSION.POLICY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_POLICY}/add`,
            `${ROUTES.PATH_POLICY_POLICY}/view/id`,
            `${ROUTES.PATH_POLICY_POLICY}/edit/id`,
          ]
        },
        {
          title: 'BENEFICIARY.TITLE',
          routerLink: [ROUTES.PATH_POLICY_BENEFICIARY],
          isActive: false,
          permission: PERMISSION.BENEFICIARY.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_BENEFICIARY}/add`,
            `${ROUTES.PATH_POLICY_BENEFICIARY}/view/id`,
            `${ROUTES.PATH_POLICY_BENEFICIARY}/edit/id`
          ]
        },
        {
          title: 'CONFIGURATION.POLICY_CLASS.TITLE',
          routerLink: [ROUTES.PATH_POLICY_CLASS],
          isActive: false,
          permission: PERMISSION.POLICY_CLASS.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_CLASS}/add`,
            `${ROUTES.PATH_POLICY_CLASS}/view/id`,
            `${ROUTES.PATH_POLICY_CLASS}/edit/id`,
            `${ROUTES.PATH_POLICY_CLASS}/clone/id`,
          ]
        },
        {
          title: 'CONFIGURATION.BENEFIT.BENEFITS',
          routerLink: [ROUTES.PATH_POLICY_BENEFIT],
          isActive: false,
          permission: PERMISSION.BENEFIT.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_POLICY_BENEFIT}/add`,
            `${ROUTES.PATH_POLICY_BENEFIT}/view/id`,
            `${ROUTES.PATH_POLICY_BENEFIT}/edit/id`,
          ]
        },
      ]
    },
    {
      title: 'PAYMENT.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-payment',
      subMenus: [
        {
          title: 'PAYMENT.CREATE.TITLE',
          routerLink: [`${ROUTES.ROUTE_PAYMENT}/create`],
          isActive: false,
          permission: [PERMISSION.PAYMENT.SCREEN],
        },
        {
          title: 'PAYMENT.LIST.TITLE',
          routerLink: [ROUTES.PATH_PAYMENT_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT.SCREEN, PERMISSION.PAYMENT.LIST],
          subPathLinks: [
            `${ROUTES.PATH_PAYMENT_TRANS}/view/id`,
            `${ROUTES.PATH_PAYMENT_TRANS}/edit/id`
          ]
        }
      ]
    },
    {
      title: 'PAYMENT_RECONCILIATION.TITLE',
      routerLink: ['./'],
      isActive: false,
      svgIconUrl: 'custom-reconciliation',
      subMenus: [
        {
          title: 'PAYMENT_RECONCILIATION.CREATE_RECONCILIATION.TITLE',
          routerLink: [`${ROUTES.ROUTE_RECONCILIATION}/create`],
          isActive: false,
          permission: [PERMISSION.PAYMENT_RECONCILIATION.SCREEN, PERMISSION.PAYMENT_RECONCILIATION.CREATE],
        },
        {
          title: 'PAYMENT_RECONCILIATION.RECONCILIATION_TRANSACTION.TITLE',
          routerLink: [ROUTES.PATH_RECONCILIATION_TRANS],
          isActive: false,
          permission: [PERMISSION.PAYMENT_RECONCILIATION.SCREEN, PERMISSION.PAYMENT_RECONCILIATION.LIST],
          subPathLinks: [`${ROUTES.PATH_RECONCILIATION_TRANS}/view/id`]
        }
      ]
    },
    {
      title: 'ADMINISTRATOR.TITLE',
      routerLink: [ROUTES.ROUTE_ADMIN],
      isActive: false,
      svgIcon: {
        maskId: 'mask0_869_4147',
        pathD: 'M5.85 17.1C6.7 16.45 7.65 15.9375 8.7 15.5625C9.75 15.1875 10.85 15 12 15C13.15 15 14.25 15.1875 15.3 15.5625C16.35 15.9375 17.3 16.45 18.15 17.1C18.7333 16.4167 19.1875 15.6417 19.5125 14.775C19.8375 13.9083 20 12.9833 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 12.9833 4.1625 13.9083 4.4875 14.775C4.8125 15.6417 5.26667 16.4167 5.85 17.1ZM12 13C11.0167 13 10.1875 12.6625 9.5125 11.9875C8.8375 11.3125 8.5 10.4833 8.5 9.5C8.5 8.51667 8.8375 7.6875 9.5125 7.0125C10.1875 6.3375 11.0167 6 12 6C12.9833 6 13.8125 6.3375 14.4875 7.0125C15.1625 7.6875 15.5 8.51667 15.5 9.5C15.5 10.4833 15.1625 11.3125 14.4875 11.9875C13.8125 12.6625 12.9833 13 12 13ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.8833 20 13.7167 19.8708 14.5 19.6125C15.2833 19.3542 16 18.9833 16.65 18.5C16 18.0167 15.2833 17.6458 14.5 17.3875C13.7167 17.1292 12.8833 17 12 17C11.1167 17 10.2833 17.1292 9.5 17.3875C8.71667 17.6458 8 18.0167 7.35 18.5C8 18.9833 8.71667 19.3542 9.5 19.6125C10.2833 19.8708 11.1167 20 12 20ZM12 11C12.4333 11 12.7917 10.8583 13.075 10.575C13.3583 10.2917 13.5 9.93333 13.5 9.5C13.5 9.06667 13.3583 8.70833 13.075 8.425C12.7917 8.14167 12.4333 8 12 8C11.5667 8 11.2083 8.14167 10.925 8.425C10.6417 8.70833 10.5 9.06667 10.5 9.5C10.5 9.93333 10.6417 10.2917 10.925 10.575C11.2083 10.8583 11.5667 11 12 11Z',
        pathFillColor: this.primaryColor
      },
      subMenus: [
        {
          title: 'ADMINISTRATOR.USER.TITLE',
          routerLink: [ROUTES.PATH_ADMIN_USERS],
          isActive: false,
          permission: [PERMISSION.USER.SCREEN, PERMISSION.USER.LIST],
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_USERS}/edit/id`,
            `${ROUTES.PATH_ADMIN_USERS}/view/id`,
            `${ROUTES.PATH_ADMIN_USERS}/add`,
          ]
        },
        {
          title: 'ADMINISTRATOR.ROLE.TITLE',
          routerLink: [`${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}`],
          isActive: false,
          permission: PERMISSION.ROLE.SCREEN,
          subPathLinks: [
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/add`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/clone/id`,
            `${ROUTES.PATH_ADMIN_ROLES}/${ROUTES.SLUG_ROLES}/edit/id`
          ]
        }
      ]
    },
    {
      title: 'ADMINISTRATOR.USER.INFO',
      routerLink: [ROUTES.ROUTE_USER_INFO],
      isActive: false,
      excludeForSidemenu: true
    },
    {
      title: 'REPORTS.TITLE',
      routerLink: [ROUTES.ROUTE_REPORTS],
      isActive: false,
      permission: [PERMISSION.REPORTS.SCREEN],
      svgIconUrl: 'custom-chart-data',
    },
    {
      title: 'NOTIFICATION.TTILE',
      routerLink: [`/${ROUTES.SLUG_NOTIFICATION}`],
      isActive: false,
      permission: PERMISSION.PAYER_NOTIFICATION.SCREEN,
      svgIconUrl: 'custom-notifications'
    },
    {
      title: 'COMMON.SETTINGS',
      routerLink: [ROUTES.ROUTE_SETTINGS],
      isActive: false,
      permission: PERMISSION.SYSTEM_SETTINGS.SCREEN,
      svgIconUrl: "settings",
      subPathLinks: [
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting`,
        `${ROUTES.ROUTE_SETTINGS}/payer-provider-setting/view`,
      ],
    }
  ];

  private patientMenuItems: MenuItem[] = [
    {
      title: 'DASHBOARD.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_DASHBOARD}`],
      isActive: false,
      permission: PERMISSION.PATIENT_DASHBOARD.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4102',
        pathD: 'M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z',
        pathFillColor: this.primaryColor
      }
    },
    {
      title: 'CLAIM.CLAIM_REIMBURSEMENT.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_REIMBURSEMENT}`],
      isActive: false,
      permission: PERMISSION.CLAIM_REIMBURSEMENT.SCREEN,
      svgIconUrl: 'hand-holding-usd',
      subMenus: [
        {
          title: 'CLAIM.CLAIM_REIMBURSEMENT.REQUEST.TITLE',
          routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_REIMBURSEMENT}/request`],
          isActive: false,
          permission: [PERMISSION.CLAIM_REIMBURSEMENT.SCREEN, PERMISSION.CLAIM_REIMBURSEMENT.CREATE],
        },
        {
          title: 'CLAIM.CLAIM_REIMBURSEMENT.HISTORY.TITLE',
          routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_REIMBURSEMENT}/history`],
          isActive: false,
          permission: [PERMISSION.CLAIM_REIMBURSEMENT.SCREEN, PERMISSION.CLAIM_REIMBURSEMENT.LIST],
          subPathLinks: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_REIMBURSEMENT}/history/view/id`],
        },
      ],
    },
    {
      title: 'PREAUTH.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_PREAUTH}`],
      isActive: false,
      permission: PERMISSION.PRE_AUTH.SCREEN,
      svgIconUrl: "custom-preauth",
      subPathLinks: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_PREAUTH}/view/id`]
    },
    {
      title: 'PRESCRIPTION.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_PRESCRIPTION}`],
      isActive: false,
      permission: PERMISSION.PRESCRIPTION.SCREEN,
      svgIconUrl: "custom-prescription",
      subPathLinks: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_PRESCRIPTION}/view/id`]
    },
    {
      title: 'CLAIM.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_CLAIMS}`],
      isActive: false,
      permission: PERMISSION.CLAIM.SCREEN,
      svgIconUrl: "custom-claim",
      subPathLinks: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_CLAIMS}/view/id`]
    },
    {
      title: 'POLICY.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_POLICY}`],
      isActive: false,
      permission: PERMISSION.POLICY.SCREEN,
      svgIcon: {
        maskId: 'mask0_869_4129',
        pathD: 'M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H16L21 8V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V9H15V5H5V19ZM7 17H17V15H7V17ZM7 9H12V7H7V9ZM7 13H17V11H7V13Z',
        pathFillColor: this.primaryColor
      },
      subPathLinks: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_POLICY}/view/id`]
    },
    {
      title: 'NOTIFICATION.TTILE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_NOTIFICATION}`],
      isActive: false,
      permission: PERMISSION.PATIENT_NOTIFICATION.SCREEN,
      svgIconUrl: 'custom-notifications'
    },
    {
      title: 'REPORTS.TITLE',
      routerLink: [ROUTES.ROUTE_REPORTS],
      isActive: false,
      permission: [PERMISSION.REPORTS.SCREEN],
      svgIconUrl: 'custom-chart-data',
    },
    {
      title: 'ADMINISTRATOR.FAQ.TITLE',
      routerLink: [`${PATIENT_PORTAL_ROUTE_PREFIX}${ROUTES.SLUG_FAQ}`],
      isActive: false,
      permission: PERMISSION.FAQ.SCREEN,
      svgIconUrl: 'faq',
    },
    {
      title: 'ADMINISTRATOR.USER.INFO',
      routerLink: [ROUTES.ROUTE_USER_INFO],
      isActive: false,
      excludeForSidemenu: true
    },
    {
      title: 'COMMON.SETTINGS',
      routerLink: [ROUTES.ROUTE_SETTINGS],
      isActive: false,
      permission: PERMISSION.SYSTEM_SETTINGS.SCREEN,
      svgIconUrl: "settings"
    }
  ];

  getAdminSideMenu(excludeExtraSidemenu?: boolean) {
    const userType = this.authService.currentUserType();
    switch (userType) {
      case UserType.TPA:
        return this.filterExcludeMenu(this.tpaMenuItems, excludeExtraSidemenu);
      case UserType.Provider:
        return this.filterExcludeMenu(this.providerMenuItems, excludeExtraSidemenu);
      case UserType.Payer:
        return this.filterExcludeMenu(this.payerMenuItems, excludeExtraSidemenu);
      case UserType.Patient:
        return this.filterExcludeMenu(this.patientMenuItems, excludeExtraSidemenu);
      default:
        return [];
    }
  }

  getAuthorizedAdminSideMenu(permissions: string[], excludeExtraSidemenu?: boolean) {
    return this.getMenuByPermission(this.getAdminSideMenu(excludeExtraSidemenu), permissions);
  }

  hasPermissionAccess(menu: MenuItem, currentPermissions: string[]): MenuItem {
    const hasPermission = (permission: string): boolean => {
      return currentPermissions.includes(permission.toLowerCase()) || this.hasMasterDataScreenAccess(permission.split('.')[0]?.toLowerCase(), currentPermissions);
    };

    if (menu.permission) {
      const haveAccess = !Array.isArray(menu.permission)
        ? hasPermission(menu.permission)
        : (menu.checkAnyPermisison ? menu.permission.some(hasPermission) : menu.permission.every(hasPermission));
      if (haveAccess)
        return menu;
    } else
      return menu;
    return null;
  }

  hasMasterDataScreenAccess(prefix: string, permissions: string[]): boolean {
    const masterComponent = ['category', 'country', 'state', 'city'];
    return prefix?.toLowerCase() === 'master-data' ? masterComponent.some(x => permissions.includes((x + '.read.screen'))) : false;
  }

  flattenMenu(menuList: MenuItem[]): MenuItem[] {
    let flatMenu: MenuItem[] = [];
    menuList.forEach((menu: MenuItem) => {
      const { subMenus, ...menuWithoutSubMenus } = menu;
      flatMenu.push(menuWithoutSubMenus);
      if (subMenus && subMenus.length > 0)
        flatMenu = flatMenu.concat(this.flattenMenu(subMenus));
    });
    return flatMenu;
  }

  private getMenuByPermission(menuList: MenuItem[], currentPermissions: string[]): MenuItem[] {
    if (!currentPermissions)
      currentPermissions = [];
    return menuList
      .map((menu: MenuItem) => {
        if (menu.subMenus) {
          const filteredSubMenus = this.getMenuByPermission(menu.subMenus, currentPermissions);
          if (filteredSubMenus.length > 0)
            return { ...menu, subMenus: filteredSubMenus };
        } else {
          if (this.authService.isSuperAdmin())
            return menu;
          else
            return this.hasPermissionAccess(menu, currentPermissions);
        }
        return null;
      }).filter((menu) => menu !== null);
  }

  private filterExcludeMenu(menuList: MenuItem[], exclude?: boolean): MenuItem[] {
    return [...menuList.filter(menu => !exclude || menu.excludeForSidemenu != true)];
  }
}